import { useCallback, useEffect, useRef, useState } from "react";
import { refreshOnboarding } from "@/pages/OnboardingChecklist";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Loader2, Camera, Sparkles, RefreshCw, Save, Plus, Trash2, Copy, Check, ClipboardList, Send, ExternalLink, Rocket, ArchiveRestore, FilePlus2, Zap, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { api, photoUrl } from "@/lib/api";
import { checkListingGoal } from "@/lib/listingGoal";
import { useUsage } from "@/lib/usage";
import { computePriceAlert, getPriceAlertThresholdPct } from "@/lib/priceAlert";
import WhatnotPushDialog from "@/pages/WhatnotPushDialog";
import QuickPastePanel from "./QuickPastePanel";
import PhotoGrid from "./draft-creator/PhotoGrid";
import AutoGenerateBar from "./draft-creator/AutoGenerateBar";
import VolumeDiscounts from "./draft-creator/VolumeDiscounts";
import PackageFields from "./draft-creator/PackageFields";
import WhatnotFormat, { PrefillWhatnot } from "./draft-creator/WhatnotFormat";
import VintedFields from "@/pages/vinted/VintedFields";
import MercariFields from "@/pages/vinted/MercariFields";
import MarketItemFields, { EMPTY_MARKET, marketPayload } from "@/pages/vinted/MarketItemFields";
import ScanToDraftButton from "./draft-creator/ScanToDraftButton";
import { useAuth } from "@/lib/auth";
import DraftsLeftRing from "@/pages/DraftsLeftRing";
import CategorySuggestions from "./draft-creator/CategorySuggestions";
import AspectsAssist from "./draft-creator/AspectsAssist";
import ConditionNotesField from "./draft-creator/ConditionNotesField";
import RecordModeCard from "./draft-creator/RecordModeCard";
import CardModeCard from "./draft-creator/CardModeCard";
import { DraftPager, libraryNeighbors } from "@/lib/draftNav";
import { useSwipe } from "@/pages/ResultNav";
import { useTeam } from "@/lib/team";
import { nextInBatch } from "@/lib/batchNext";
import BatchLeft from "@/pages/batch/BatchLeft";
import { useDupGuard } from "./draft-creator/DupWarning";

const CONDITIONS = [
  "New",
  "New (Other)",
  "New with Defects",
  "Certified Refurbished",
  "Excellent Refurbished",
  "Very Good Refurbished",
  "Good Refurbished",
  "Seller Refurbished",
  "Used - Like New",
  "Used - Very Good",
  "Used - Good",
  "Used - Acceptable",
  "For Parts or Not Working",
];

function CopyButton({ text, label = "Copy", testid }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    if (!text) {
      toast.error("Nothing to copy yet");
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <button
      type="button"
      onClick={doCopy}
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-mono uppercase tracking-wider transition-colors ${
        copied ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : label}
    </button>
  );
}

function formatFullDraft(d) {
  const specifics = (d.item_specifics || []).map((s) => `• ${s.key}: ${s.value}`).join("\n");
  return [
    `TITLE (${(d.title || "").length}/80):\n${d.title || ""}`,
    `\nCATEGORY:\n${d.category || ""}`,
    `\nCONDITION:\n${d.condition || ""}`,
    `\nPRICE (USD):\n${d.price || ""}`,
    `\nQUANTITY:\n${d.quantity || "1"}`,
    `\nPROMOTED LISTINGS AD RATE:\n${d.promotion_rate || ""}%`,
    `\nSKU:\n${d.sku || ""}`,
    `\nITEM SPECIFICS:\n${specifics}`,
    `\nDESCRIPTION:\n${d.description || ""}`,
  ].join("\n");
}

const PKG_KEYS = ["weight_lb", "weight_oz", "length_in", "width_in", "height_in"];
const pkgDefaults = (d) => Object.fromEntries(PKG_KEYS.map((k) => [k, d[k] ?? ""]));
const numOnly = (v) => v.replace(/[^0-9.]/g, "").slice(0, 5);

export default function DraftCreator() {
  const { isAssistant, canList, canPhotos } = useTeam();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh: refreshUsage } = useUsage();
  const editId = searchParams.get("edit");
  const [photos, setPhotos] = useState([]); // {id, storage_path, filename}
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [autoGen, setAutoGen] = useState(() => localStorage.getItem("snaplist.autoGenerate") === "1");
  const [autoIn, setAutoIn] = useState(0);
  const [hint, setHint] = useState("");
  const [draft, setDraft] = useState(null);
  // "Snap to check stock" → "Start a draft from this photo" lands here with ?photo=<id>.
  useEffect(() => {
    const pid = searchParams.get("photo");
    if (!pid) return;
    setPhotos((p) => (p.some((x) => x.id === pid) ? p : [...p, { id: pid, filename: "photo.jpg" }]));
    const next = new URLSearchParams(searchParams); next.delete("photo"); setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  // Research hand-off — whenever the creator has unsaved photos or a title
  // the user may want to tap "Research" in the nav and keep the same item
  // loaded. We sync a lightweight snapshot to sessionStorage; Research.jsx
  // consumes it on mount if its own photos list is empty, then clears it.
  const RESEARCH_HANDOFF_KEY = "snaplist.researchHandoff";
  useEffect(() => {
    try {
      if (photos.length > 0) {
        sessionStorage.setItem(
          RESEARCH_HANDOFF_KEY,
          JSON.stringify({
            photos: photos.map((p) => ({
              id: p.id,
              filename: p.filename || "",
              rotated_at: p.rotated_at || null,
            })),
            hint: (draft?.title || hint || "").trim(),
          })
        );
      } else {
        sessionStorage.removeItem(RESEARCH_HANDOFF_KEY);
      }
    } catch { /* ignore storage errors */ }
  }, [photos, draft?.title, hint]);
  // Prefill — Price, Min offer (USD), Ad rate %, SKU. All four are seeded
  // from localStorage so each new listing starts with the user's last-used
  // defaults. The SKU stays identical until the user hits Reset in Settings.
  const PREFILL_DEFAULTS_KEY = "snaplist.prefillDefaults";
  const LAST_SETTINGS_KEY = "snaplist.lastListingSettings";

  const [prefill, setPrefill] = useState(() => {
    let defaults = {};
    try { defaults = JSON.parse(localStorage.getItem(PREFILL_DEFAULTS_KEY) || "{}"); } catch { /* ignore */ }
    return {
      price: "",
      quantity: "1",
      item_cost: "",
      whatnot_format: defaults.whatnot_format === "auction" ? "auction" : "buy_it_now",
      auction_start: "",
      min_offer: defaults.min_offer ?? "",
      promotion_rate: "3",
      sku: defaults.sku ?? "",
      ...pkgDefaults(defaults),
    };
  });
  const [saving, setSaving] = useState(false);
  const [dupGuard, dupDialog] = useDupGuard();
  const [pushing, setPushing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [lastApplied, setLastApplied] = useState(null); // {original, applied}
  const [aspects, setAspects] = useState(null); // {category_id, aspects: [{name, required, mode, cardinality, values}]}
  const [loadingAspects, setLoadingAspects] = useState(false);
  const fileRef = useRef(null);

  // Fetch required item specifics whenever category_id changes.
  useEffect(() => {
    const catId = (draft?.category_id || "").trim();
    if (!catId || !/^\d+$/.test(catId)) {
      setAspects(null);
      return;
    }
    if (aspects?.category_id === catId) return;
    let cancelled = false;
    setLoadingAspects(true);
    (async () => {
      try {
        const { data } = await api.get(`/ebay/aspects/${catId}`);
        if (!cancelled) setAspects(data);
      } catch (e) {
        if (!cancelled) {
          setAspects(null);
          const detail = e?.response?.data?.detail || "";
          // Silent when disconnected; noisy toasts would spam edit flow.
          if (!String(detail).toLowerCase().includes("connect ebay")) {
            console.warn("aspects fetch failed:", detail);
          }
        }
      } finally {
        if (!cancelled) setLoadingAspects(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.category_id]);

  // Load an existing draft when arriving from Library via /?edit=<id>
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      setLoadingDraft(true);
      try {
        const { data } = await api.get(`/drafts/${editId}`);
        if (cancelled) return;
        loadedSnap.current = JSON.stringify(data);
        setDraft(data);
        setPhotos(data.photos || []);
        setHint(data.hint || "");
        toast.success(data.title ? `Editing "${data.title.slice(0, 40)}"` : "Write this listing. The photos are attached.");
      } catch (e) {
        if (!cancelled) toast.error("Could not load draft — it may have been deleted");
        // Clear the query param so a stale ?edit doesn't persist
        setSearchParams({}, { replace: true });
      } finally {
        if (!cancelled) setLoadingDraft(false);
      }
    })();
    return () => { cancelled = true; };
  }, [editId, setSearchParams]);

  const upload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files).slice(0, 6 - photos.length)) {
      try {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/photos/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setPhotos((p) => [...p, ...uploaded]);
    setUploading(false);
    if (uploaded.length) {
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} added`);
      refreshOnboarding();
    }
  }, [photos.length]);

  const onFilePick = (e) => upload(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    upload(e.dataTransfer.files);
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const [bgRemovingId, setBgRemovingId] = useState(null);

  const removeBackground = useCallback(async (id) => {
    setBgRemovingId(id);
    try {
      const { data } = await api.post(`/photos/${id}/remove-background`);
      // Swap the photo in place, preserving order
      setPhotos((prev) => prev.map((p) => (p.id === id ? { ...data } : p)));
      toast.success("Background cleaned to white");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Background removal failed");
    } finally {
      setBgRemovingId(null);
    }
  }, []);

  const revertBackground = useCallback((photo) => {
    if (!photo?.parent_id) return;
    // Swap this edited photo back to its original by id.
    setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { id: photo.parent_id, filename: photo.filename.replace(/^nobg-/, "") } : p)));
    toast.success("Reverted to original");
  }, []);

  const [rotatingId, setRotatingId] = useState(null);
  const rotatePhoto = useCallback(async (id, direction) => {
    setRotatingId(id);
    try {
      const { data } = await api.post(`/photos/${id}/rotate`, null, { params: { direction } });
      // Patch the photo in place so the `?v=rotated_at` cache-bust fires
      // immediately on the thumbnail URL and the server-side rotated image is
      // picked up without a page refresh.
      setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, rotated_at: data.rotated_at } : p)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    } finally {
      setRotatingId(null);
    }
  }, []);

  const generate = async () => {
    if (photos.length === 0) {
      toast.error("Add at least one photo first");
      return;
    }
    setGenerating(true);
    setDraft(null);
    try {
      const { data } = await api.post("/drafts/generate", {
        photo_ids: photos.map((p) => p.id),
        hint,
      });
      // User-entered pre-fill values take precedence over the AI's guesses.
      const merged = { ...data };
      let last = null;
      try { last = JSON.parse(localStorage.getItem(LAST_SETTINGS_KEY) || "null"); } catch { /* ignore */ }
      if (last && (last.category || last.condition)) {
        const original = { category: merged.category, category_id: merged.category_id, condition: merged.condition };
        if (last.category) { merged.category = last.category; merged.category_id = last.category_id; }
        if (last.condition) merged.condition = last.condition;
        setLastApplied({ original, applied: last });
      } else {
        setLastApplied(null);
      }
      for (const key of ["price", "quantity", "min_offer", "promotion_rate", "sku", "item_cost"]) {
        const v = (prefill[key] ?? "").toString().trim();
        if (v) merged[key] = v;
      }
      merged.whatnot_format = prefill.whatnot_format === "auction" ? "auction" : "buy_it_now";
      Object.assign(merged, marketPayload(prefill.market));
      if (merged.whatnot_format === "auction") {
        const start = (prefill.auction_start || "").trim() || (prefill.price || "").trim();
        if (start) merged.auction_start = start;
      }
      const pkgIn = Object.fromEntries(PKG_KEYS.map((k) => [k, String(prefill[k] ?? "").trim()]));
      if (pkgIn.weight_lb || pkgIn.weight_oz) {
        merged.package = { ...(merged.package || {}), weight_lb: Number(pkgIn.weight_lb) || 0, weight_oz: Number(pkgIn.weight_oz) || 0 };
      }
      for (const k of ["length_in", "width_in", "height_in"]) {
        if (pkgIn[k]) merged.package = { ...(merged.package || {}), [k]: Number(pkgIn[k]) || 0 };
      }
      setDraft(merged);
      // Bump the header "N/15 this month" chip immediately — the backend
      // already counted this generation; this just re-pulls so the UI stays
      // honest without waiting for a page refresh.
      refreshUsage();
      // Persist last-used defaults so next listing opens with them.
      // Quantity intentionally NOT remembered — always resets to 1 each draft.
      // Ad rate intentionally NOT remembered — always resets to 3% each draft.
      try {
        const existing = JSON.parse(localStorage.getItem(PREFILL_DEFAULTS_KEY) || "{}");
        const next = { ...existing };
        // Scrub legacy quantity/ad_rate defaults from older versions.
        delete next.quantity;
        delete next.ad_rate;
        const minOffer = (prefill.min_offer ?? "").toString().trim();
        const skuUsed = (merged.sku ?? "").toString().trim();
        if (minOffer) next.min_offer = minOffer;
        if (skuUsed) next.sku = skuUsed;
        next.whatnot_format = prefill.whatnot_format === "auction" ? "auction" : "buy_it_now";
        // Package weight/size stay remembered until the user changes or clears them.
        for (const k of PKG_KEYS) {
          const v = String(prefill[k] ?? "").trim();
          if (v) next[k] = v; else delete next[k];
        }
        localStorage.setItem(PREFILL_DEFAULTS_KEY, JSON.stringify(next));
      } catch { /* ignore */ }
      toast.success("Draft generated by Gemini 3 Flash");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "string" ? d : d?.message) || e.message || "Generation failed";
      toast.error(String(msg));
      if (e?.response?.status === 402) refreshUsage();
    } finally {
      setGenerating(false);
    }
  };
  // Auto-generate: once photos stop arriving, count down 3s then generate (cancellable).
  const photoCount = photos.length;
  useEffect(() => {
    if (!autoGen || draft || generating || uploading || photoCount === 0) { setAutoIn(0); return; }
    setAutoIn(3);
  }, [autoGen, draft, generating, uploading, photoCount]);
  useEffect(() => {
    if (!autoIn) return undefined;
    const t = setTimeout(() => {
      if (autoIn === 1) { setAutoIn(0); generate(); } else setAutoIn(autoIn - 1);
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoIn]);


  const patch = (patchObj) => setDraft((d) => (d ? { ...d, ...patchObj } : d));
  const patchSpecific = (idx, key, value) =>
    setDraft((d) => {
      const list = [...(d.item_specifics || [])];
      list[idx] = { ...list[idx], [key]: value };
      return { ...d, item_specifics: list };
    });
  const addSpecific = () =>
    setDraft((d) => ({ ...d, item_specifics: [...(d?.item_specifics || []), { key: "", value: "" }] }));
  const removeSpecific = (idx) =>
    setDraft((d) => ({ ...d, item_specifics: (d.item_specifics || []).filter((_, i) => i !== idx) }));

  const [navBusy, setNavBusy] = useState(false);
  const loadedSnap = useRef("");
  const draftNav = editId ? libraryNeighbors(editId) : { index: -1, total: 0 };
  const goToDraft = async (id) => {
    if (!id || navBusy) return;
    setNavBusy(true);
    try {
      if (draft && JSON.stringify(draft) !== loadedSnap.current) await api.post("/drafts", draft);
      setSearchParams({ edit: id });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Couldn't save this draft — fix it before moving on");
    } finally {
      setNavBusy(false);
    }
  };
  const draftSwipe = useSwipe(() => goToDraft(draftNav.next), () => goToDraft(draftNav.prev));

  const saveDraft = async () => {
    if (!draft || !(await dupGuard(draft))) return;
    setSaving(true);
    try {
      await api.post("/drafts", draft);
      toast.success("Draft saved to Library");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const pushToEbay = async () => {
    if (!draft || !(await dupGuard(draft))) return;
    setPushing(true);
    try {
      // Ensure the draft is saved first so backend can find it by id
      await api.post("/drafts", draft);
      const catId = (draft?.category_id || "").trim() || undefined;
      const { data } = await api.post("/ebay/push", { draft_id: draft.id, category_id: catId });
      toast.success(`Pushed to eBay as unpublished — offer ${data.offer_id}`, {
        action: {
          label: "Seller Hub",
          onClick: () => window.open(data.seller_hub_url, "_blank", "noopener,noreferrer"),
        },
      });
      rememberLastSettings();
      setDraft((d) => ({ ...d, ebay: { offer_id: data.offer_id, status: "UNPUBLISHED", sku: data.sku, category_id: catId || d?.ebay?.category_id } }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Push failed";
      toast.error(String(msg));
    } finally {
      setPushing(false);
    }
  };

  const publishOnEbay = async () => {
    if (!draft || !(await dupGuard(draft))) return;
    setPublishing(true);
    try {
      // Save the draft first so backend can find it by id
      await api.post("/drafts", draft);
      const catId = (draft?.category_id || "").trim() || undefined;
      const { data } = await api.post("/ebay/publish", { draft_id: draft.id, category_id: catId });
      const finishedId = draft.id;
      if (!data.already_published) checkListingGoal();
      completeListing(
        data.already_published ? "Already published on eBay" : `Live on eBay — listing ${data.listing_id || "created"}`,
        data.listing_url || null,
        finishedId,
      );
      // Surface Promoted Listings result (sell.marketing scope / Marketing API)
      if (data.promoted) {
        if (data.promoted.ok) {
          toast.success(`Promoted at ${data.promoted.bid_percentage}% ad rate`, { duration: 6000 });
        } else {
          const err = String(data.promoted.error || "").toLowerCase();
          if (err.includes("sell.marketing") || err.includes("scope")) {
            toast.warning(
              "Promotion rate didn't apply — reconnect eBay in Settings to grant Promoted Listings access.",
              { duration: 10000 }
            );
          } else {
            toast.warning(`Promotion didn't apply: ${data.promoted.error}`, { duration: 10000 });
          }
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Publish failed";
      toast.error(String(msg));
    } finally {
      setPublishing(false);
    }
  };

  const [pushingWhatnot, setPushingWhatnot] = useState(false);
  const [whatnotDialog, setWhatnotDialog] = useState(false);
  const isAdmin = !!useAuth().user?.is_admin;
  const pushToWhatnot = async () => {
    if (!draft) return;
    setPushingWhatnot(true);
    try {
      await api.post("/drafts", draft);
      const { data } = await api.post("/whatnot/push", { draft_id: draft.id });
      completeListing(
        data.already_pushed ? "Already on Whatnot" : `Pushed to Whatnot — ${data.status === "PUBLISHED" ? "live" : "draft"}`,
        data.hub_url || null,
        draft.id,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Push to Whatnot failed";
      toast.error(String(msg), {
        duration: 10000,
        action: String(msg).toLowerCase().includes("not connected")
          ? { label: "Open settings", onClick: () => document.querySelector('[data-testid="open-whatnot-settings"]')?.click() }
          : undefined,
      });
    } finally {
      setPushingWhatnot(false);
      setWhatnotDialog(false);
    }
  };

  const withdrawFromEbay = async () => {
    if (!draft?.ebay?.offer_id) return;
    if (!window.confirm("End this live listing on eBay?")) return;
    setPublishing(true);
    try {
      await api.post("/ebay/withdraw", { draft_id: draft.id });
      toast.success("Listing withdrawn");
      setDraft((d) => ({ ...d, ebay: { ...(d.ebay || {}), status: "UNPUBLISHED", listing_id: undefined, listing_url: undefined } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Withdraw failed");
    } finally {
      setPublishing(false);
    }
  };

  const undoLastSettings = () => {
    if (!lastApplied) return;
    setDraft((d) => (d ? { ...d, ...lastApplied.original } : d));
    setLastApplied(null);
    toast.message("Restored the AI's category and condition");
  };

  const resetAll = () => {
    setPhotos([]);
    setDraft(null);
    setLastApplied(null);
    setHint("");
    // Keep user's remembered defaults — only clear per-listing fields.
    // SKU stays identical to the saved default — user resets it manually
    // from Settings when they want a new value.
    let defaults = {};
    try { defaults = JSON.parse(localStorage.getItem(PREFILL_DEFAULTS_KEY) || "{}"); } catch { /* ignore */ }
    setPrefill({
      price: "",
      quantity: "1",
      item_cost: "",
      whatnot_format: defaults.whatnot_format === "auction" ? "auction" : "buy_it_now",
      auction_start: "",
      min_offer: defaults.min_offer ?? "",
      promotion_rate: "3",
      sku: defaults.sku ?? "",
      ...pkgDefaults(defaults),
    });
  };

  const [readyPulse, setReadyPulse] = useState(false);
  const topRef = useRef(null);
  const rememberLastSettings = () => {
    if (!draft?.category && !draft?.condition) return;
    const last = { category: draft.category || "", category_id: draft.category_id || "", condition: draft.condition || "" };
    try { localStorage.setItem(LAST_SETTINGS_KEY, JSON.stringify(last)); } catch { /* ignore */ }
  };
  // Instant + element-based so it works whichever element scrolls (iOS PWA) and isn't cancelled by the picker.
  const scrollToUploader = () => {
    const jump = () => {
      topRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
    };
    jump();
    requestAnimationFrame(jump);
    setTimeout(jump, 350);
    // iOS can restore the old scroll spot when the photo picker closes.
    window.addEventListener("focus", () => setTimeout(jump, 50), { once: true });
  };
  const startNextListing = () => {
    resetAll();
    setSearchParams({}, { replace: true });
    scrollToUploader();
    setReadyPulse(true);
    setTimeout(() => setReadyPulse(false), 2400);
    // Browsers may block this without a fresh tap; the pulsing dropzone is the fallback.
    try { fileRef.current?.click(); } catch { /* ignore */ }
  };

  // Publish/Whatnot success → reset for the next upload, keep a link to the finished one.
  const openNextInBatch = async (batchId, finishedId, note = "Next item in this batch is open") => {
    try {
      const next = await nextInBatch(batchId, [finishedId, editId]);
      if (next) {
        resetAll();
        setSearchParams({ edit: next.draft_id, batch: batchId });
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.message(note, { duration: 2500 });
        return;
      }
    } catch { /* fall back to the batch page */ }
    navigate(`/batch?b=${batchId}`);
  };

  const completeListing = (message, viewUrl, finishedId) => {
    rememberLastSettings();
    refreshOnboarding();
    toast.success(message, {
      duration: 10000,
      action: viewUrl
        ? { label: "View listing", onClick: () => window.open(viewUrl, "_blank", "noopener,noreferrer") }
        : { label: "Open draft", onClick: () => setSearchParams({ edit: finishedId }) },
    });
    const batchId = searchParams.get("batch") || draft?.photo_batch_id;
    if (batchId) { openNextInBatch(batchId, finishedId); return; } // stay inside the batch: next unposted item opens
    if (searchParams.get("from") === "queue") { navigate("/queue?next=1"); return; } // lister flow: straight to the next ready item
    startNextListing();
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px,1fr]" {...(draftNav.total > 1 ? draftSwipe : {})}>
      {draftNav.total > 1 && <div className="lg:col-span-2"><DraftPager nav={draftNav} onGo={goToDraft} busy={navBusy} /></div>}
      {dupDialog}
      {/* LEFT: Photo + AI controls */}
      <section className="min-w-0 space-y-4">
        <div ref={topRef} className="scroll-mt-32">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{canPhotos ? "Draft a listing." : "Check it, then post."}</h1>
          <p className="mt-1.5 max-w-md text-sm text-slate-600">
            {canPhotos ? "Snap product photos, let Gemini 3 Flash write the eBay listing, then one-tap copy fields into the eBay app."
              : "Fix the title, price and details below, then tap Post to eBay. The next item in your queue opens after it posts."}
          </p>
          <div className="mt-3"><DraftsLeftRing size={48} testid="create-drafts-left-ring" /></div>
        </div>

        {canPhotos && <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className={`uploader-dropzone rounded-lg p-4 text-center transition-shadow duration-300 ${readyPulse ? "animate-pulse ring-4 ring-[#0053DA]/40" : ""}`}
          data-testid="uploader-dropzone"
          data-ready={readyPulse ? "true" : "false"}
        >
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm">
            <Camera className="h-5 w-5 text-[#0053DA]" />
          </div>
          <p className="mt-3 text-sm font-medium text-slate-900">Drop photos or</p>
          <div className="mt-2 flex flex-wrap justify-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || photos.length >= 6}
              data-testid="upload-photo-button"
              className="h-9 rounded-full"
            >
              {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
              Upload
            </Button>
            <ScanToDraftButton onCreated={(id) => setSearchParams({ edit: id })} disabled={uploading} />
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-slate-500">
              {photos.length}/6
            </span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={onFilePick}
            className="hidden"
            data-testid="photo-file-input"
          />
        </div>}

        {photos.length > 0 && (
          <PhotoGrid photos={photos} setPhotos={setPhotos} removePhoto={removePhoto} onRemoveBackground={removeBackground} onRevertBackground={revertBackground} onRotate={rotatePhoto} bgRemovingId={bgRemovingId} rotatingId={rotatingId} saveName={draft?.title} />
        )}

        {canPhotos && <><AutoGenerateBar on={autoGen} countdown={autoIn} disabled={generating}
          onToggle={(v) => { setAutoGen(v); localStorage.setItem("snaplist.autoGenerate", v ? "1" : "0"); if (!v) setAutoIn(0); }}
          onCancel={() => setAutoIn(0)} />
        <Button
          onClick={generate}
          disabled={generating || photos.length === 0}
          className={`h-12 w-full rounded-full bg-[#0053DA] text-base font-semibold text-white hover:bg-[#0041B0] ${generating ? "scan-pulse" : ""}`}
          data-testid="generate-draft-button"
        >
          {generating ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing photos…</>
          ) : (
            <><Sparkles className="mr-2 h-4 w-4" /> Generate listing</>
          )}
        </Button></>}
        {photos.length > 0 && !draft && (
          <p className="-mt-2 text-center text-[11px] text-slate-400" data-testid="generate-hint">
            Optional: add a price, context or box size below first — they override the AI.
          </p>
        )}

        {/* Pre-fill — appears after upload, before AI generation. Values entered
            here take precedence over anything the AI returns. */}
        {photos.length > 0 && !draft && (
          <div
            className="rounded-lg border border-slate-200 bg-white p-3"
            data-testid="prefill-panel"
          >
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">
                Pre-fill before AI (optional)
              </Label>
              <span className="text-[10px] text-slate-400">overrides AI · min offer, weight, box & SKU remembered · qty & ad rate reset each draft</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="prefill-price" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Price (USD)
                </Label>
                <Input
                  id="prefill-price"
                  value={prefill.price}
                  onChange={(e) => setPrefill((p) => ({ ...p, price: e.target.value }))}
                  placeholder="e.g. 24.99"
                  inputMode="decimal"
                  className="mt-1 h-8 font-mono text-xs"
                  data-testid="prefill-price-input"
                />
              </div>
              <div>
                <Label htmlFor="prefill-quantity" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Quantity
                </Label>
                <div className="mt-1 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPrefill((p) => ({ ...p, quantity: String(Math.max(1, (parseInt(p.quantity, 10) || 1) - 1)) }))}
                    className="flex h-8 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-40"
                    data-testid="prefill-quantity-dec"
                    aria-label="Decrease quantity"
                    disabled={(parseInt(prefill.quantity, 10) || 1) <= 1}
                  >
                    −
                  </button>
                  <Input
                    id="prefill-quantity"
                    value={prefill.quantity}
                    onChange={(e) => setPrefill((p) => ({ ...p, quantity: e.target.value.replace(/[^0-9]/g, "") }))}
                    placeholder="1"
                    inputMode="numeric"
                    className="h-8 flex-1 text-center font-mono text-xs"
                    data-testid="prefill-quantity-input"
                  />
                  <button
                    type="button"
                    onClick={() => setPrefill((p) => ({ ...p, quantity: String((parseInt(p.quantity, 10) || 0) + 1) }))}
                    className="flex h-8 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50"
                    data-testid="prefill-quantity-inc"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
                <p className="mt-0.5 text-[9px] font-mono text-slate-400">
                  how many units for sale
                </p>
              </div>
              <PrefillWhatnot prefill={prefill} setPrefill={setPrefill} />
              <div className="col-span-2">
                <MarketItemFields value={prefill.market || EMPTY_MARKET} onChange={(market) => setPrefill((p) => ({ ...p, market }))} testid="prefill-market" />
              </div>
              <div>
                <Label htmlFor="prefill-item-cost" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Item cost (USD)
                </Label>
                <Input
                  id="prefill-item-cost"
                  value={prefill.item_cost}
                  onChange={(e) => setPrefill((p) => ({ ...p, item_cost: e.target.value.replace(/[^0-9.]/g, "") }))}
                  placeholder="What you paid"
                  inputMode="decimal"
                  className="mt-1 h-8 font-mono text-xs"
                  data-testid="prefill-item-cost-input"
                />
                <p className="mt-0.5 text-[9px] font-mono text-slate-400">profit tracking + Whatnot auction start</p>
              </div>
              <div>
                <Label htmlFor="prefill-min-offer" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Min offer (USD)
                </Label>
                <Input
                  id="prefill-min-offer"
                  value={prefill.min_offer}
                  onChange={(e) => setPrefill((p) => ({ ...p, min_offer: e.target.value }))}
                  placeholder="auto-decline below"
                  inputMode="decimal"
                  className="mt-1 h-8 font-mono text-xs"
                  data-testid="prefill-min-offer-input"
                />
                {(() => {
                  const price = parseFloat(prefill.price);
                  const minOffer = parseFloat(prefill.min_offer);
                  if (Number.isNaN(price) || Number.isNaN(minOffer) || price <= 0 || minOffer <= 0 || minOffer >= price) return null;
                  return (
                    <p className="mt-0.5 text-[9px] font-mono text-slate-400" data-testid="prefill-min-offer-pct">
                      = {((minOffer / price) * 100).toFixed(0)}% of price
                    </p>
                  );
                })()}
              </div>
              <div>
                <Label htmlFor="prefill-ad-rate" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Ad rate (%)
                </Label>
                <Input
                  id="prefill-ad-rate"
                  value={prefill.promotion_rate}
                  onChange={(e) => setPrefill((p) => ({ ...p, promotion_rate: e.target.value }))}
                  placeholder="e.g. 5"
                  inputMode="decimal"
                  className="mt-1 h-8 font-mono text-xs"
                  data-testid="prefill-ad-rate-input"
                />
              </div>
              <div>
                <Label htmlFor="prefill-weight-lb" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Weight
                </Label>
                <div className="mt-1 grid grid-cols-2 gap-1">
                  <Input
                    id="prefill-weight-lb"
                    value={prefill.weight_lb}
                    onChange={(e) => setPrefill((p) => ({ ...p, weight_lb: e.target.value.replace(/[^0-9]/g, "") }))}
                    placeholder="lb"
                    aria-label="Weight pounds"
                    inputMode="numeric"
                    className="h-8 px-2 text-center font-mono text-xs"
                    data-testid="prefill-weight-lb-input"
                  />
                  <Input
                    value={prefill.weight_oz}
                    onChange={(e) => setPrefill((p) => ({ ...p, weight_oz: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) }))}
                    placeholder="oz"
                    aria-label="Weight ounces"
                    inputMode="numeric"
                    className="h-8 px-2 text-center font-mono text-xs"
                    data-testid="prefill-weight-oz-input"
                  />
                </div>
                <p className="mt-0.5 text-[9px] font-mono text-slate-400">packed · remembered</p>
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  Box size (in)
                </Label>
                <div className="mt-1 grid grid-cols-3 gap-1">
                  {[["length_in", "L"], ["width_in", "W"], ["height_in", "H"]].map(([k, ph]) => (
                    <Input
                      key={k}
                      value={prefill[k]}
                      onChange={(e) => setPrefill((p) => ({ ...p, [k]: numOnly(e.target.value) }))}
                      placeholder={ph}
                      aria-label={`Box ${ph}`}
                      inputMode="decimal"
                      className="h-8 px-2 text-center font-mono text-xs"
                      data-testid={`prefill-box-${k.split("_")[0]}-input`}
                    />
                  ))}
                </div>
                <p className="mt-0.5 text-[9px] font-mono text-slate-400">remembered until changed · blank = AI guess</p>
              </div>
              <div>
                <Label htmlFor="prefill-sku" className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  SKU
                </Label>
                <Input
                  id="prefill-sku"
                  value={prefill.sku}
                  onChange={(e) => setPrefill((p) => ({ ...p, sku: e.target.value }))}
                  placeholder="custom SKU"
                  className="mt-1 h-8 font-mono text-xs"
                  data-testid="prefill-sku-input"
                />
                <p className="mt-0.5 text-[9px] font-mono text-slate-400">
                  stays the same until you reset in Settings
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="hint" className="text-xs font-mono uppercase tracking-wider text-slate-500">
            Optional context
          </Label>
          <Textarea
            id="hint"
            placeholder="e.g. brand, model number, defects, included accessories…"
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            className="min-h-[80px] resize-none"
            data-testid="hint-input"
          />
        </div>


        {draft && (
          <div className="flex gap-2">
            {canPhotos && <Button
              onClick={generate}
              variant="outline"
              className="h-10 flex-1 rounded-full"
              data-testid="regenerate-button"
              disabled={generating}
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
            </Button>}
            <Button
              onClick={resetAll}
              variant="ghost"
              className="h-10 rounded-full text-slate-500 hover:text-slate-900"
              data-testid="reset-button"
            >
              Reset
            </Button>
          </div>
        )}
      </section>

      {/* RIGHT: Draft form */}
      <section className="min-w-0">
        {!draft && !generating && (
          <div className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white p-8 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-50">
              <ClipboardList className="h-5 w-5 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-900">Your draft appears here</p>
            <p className="mt-1 max-w-xs text-xs text-slate-500">
              Upload up to 6 photos and hit <span className="font-mono">Generate</span>. All fields are editable.
            </p>
          </div>
        )}

        {generating && (
          <div className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-lg border border-slate-200 bg-white p-8 text-center slide-in">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#0053DA]/10 scan-pulse">
              <Sparkles className="h-5 w-5 text-[#0053DA]" />
            </div>
            <p className="text-sm font-semibold text-slate-900">Gemini 3 Flash is drafting…</p>
            <p className="mt-1 text-xs text-slate-500">Analyzing photos, condition, keywords, pricing.</p>
          </div>
        )}

        {draft && (
          <div className="space-y-5 rounded-lg border border-slate-200 bg-white p-4 sm:p-6 slide-in" data-testid="draft-panel">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                {!draft.manual ? <Badge className="bg-[#0053DA] text-white hover:bg-[#0041B0]">AI Generated</Badge>
                  : <Badge variant="outline" className="border-slate-300 text-slate-600" data-testid="draft-manual-badge">Write it yourself</Badge>}
                <span className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  {new Date(draft.updated_at || draft.created_at).toLocaleString()}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <CopyButton text={formatFullDraft(draft)} label="Copy all" testid="copy-all-button" />
                <Button
                  onClick={saveDraft}
                  disabled={saving}
                  size="sm"
                  className="h-8 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                  data-testid="save-draft-button"
                >
                  {saving ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Save className="mr-1 h-3 w-3" />}
                  Save
                </Button>
                {draft.ebay?.status === "PUBLISHED" && draft.ebay?.api === "trading" ? (
                  <>
                    <Button
                      onClick={startNextListing}
                      size="sm"
                      className="h-8 rounded-full bg-[#0053DA] text-white hover:bg-[#0041B0]"
                      data-testid="next-listing-button"
                    >
                      <FilePlus2 className="mr-1 h-3 w-3" /> Next listing
                    </Button>
                    <Button
                      onClick={withdrawFromEbay}
                      disabled={publishing}
                      size="sm"
                      variant="outline"
                      className="h-8 rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid="withdraw-ebay-button"
                    >
                      {publishing ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <ArchiveRestore className="mr-1 h-3 w-3" />}
                      Withdraw
                    </Button>
                  </>
                ) : (
                  <>
                    {canList && <Button
                      onClick={publishOnEbay}
                      disabled={publishing || pushing}
                      size="sm"
                      className="h-8 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid="publish-ebay-button"
                    >
                      {publishing ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Rocket className="mr-1 h-3 w-3" />}
                      {draft.ebay?.status === "PUBLISHED" ? "Re-post (mobile-editable)" : "Post to eBay"}
                    </Button>}
                    {searchParams.get("batch") && draft.id && (
                      <BatchLeft batchId={searchParams.get("batch")} draftId={draft.id}
                        onSkip={async () => { try { await api.post("/drafts", draft); } catch { /* keep going */ } openNextInBatch(searchParams.get("batch"), draft.id, "Skipped for now · it's at the back of the batch"); }} />
                    )}
                    {isAdmin ? <WhatnotPushDialog open={whatnotDialog} onOpenChange={setWhatnotDialog}
                      drafts={whatnotDialog ? [draft] : []} onConfirm={pushToWhatnot} busy={pushingWhatnot} /> : null}
                    {isAdmin ? <Button
                      onClick={async () => { try { await api.post("/drafts", draft); } catch { /* push saves again */ } setWhatnotDialog(true); }}
                      disabled={pushingWhatnot}
                      size="sm"
                      className="h-8 rounded-full bg-amber-500 text-white hover:bg-amber-600"
                      data-testid="push-whatnot-button"
                      title="Push to Whatnot"
                    >
                      {pushingWhatnot ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Zap className="mr-1 h-3 w-3" />}
                      {draft.whatnot?.product_id ? "On Whatnot" : "Whatnot"}
                    </Button> : null}
                    <QuickPastePanel
                      draft={draft}
                      trigger={
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 rounded-full border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          data-testid="open-quick-paste"
                          title="Open the Quick paste panel to copy fields into Mercari, Poshmark, Depop, FB Marketplace, Vinted, Craigslist or Amazon"
                        >
                          <ShoppingBag className="mr-1 h-3 w-3" /> Quick paste
                        </Button>
                      }
                    />
                  </>
                )}
              </div>
            </div>

            {draft.ebay?.offer_id && (
              <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs">
                <span className="font-mono text-emerald-800">
                  eBay offer <span className="font-semibold">{draft.ebay.offer_id}</span> · {draft.ebay.status}
                  {draft.ebay.listing_id ? <> · listing <span className="font-semibold">{draft.ebay.listing_id}</span></> : null}
                </span>
                <a
                  href={draft.ebay.listing_url || (draft.ebay.env === "sandbox" ? "https://www.sandbox.ebay.com/sh/lst/drafts" : "https://www.ebay.com/sh/lst/drafts")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-emerald-800 hover:underline"
                >
                  {draft.ebay.status === "PUBLISHED" ? "View listing" : "Open in eBay"} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}

            {/* Title */}
            <FieldRow label="Title" hint={`${(draft.title || "").length}/80`} testid="field-title">
              <div className="flex items-start gap-2">
                {/* Wraps so the full 80-char title is visible/scrollable on phones; newlines stripped. */}
                <Textarea
                  value={draft.title || ""}
                  maxLength={80}
                  rows={3}
                  onChange={(e) => patch({ title: e.target.value.replace(/[\r\n]+/g, " ") })}
                  onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                  className="min-h-0 resize-none overflow-y-auto leading-snug"
                  data-testid="input-title"
                />
                <CopyButton text={draft.title} label="Copy" testid="copy-title-button" />
              </div>
            </FieldRow>

            {lastApplied ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#0053DA]/20 bg-[#0053DA]/5 px-3 py-2 text-xs text-slate-700" data-testid="last-settings-applied">
                <span className="min-w-0">
                  <RefreshCw className="mr-1 inline h-3.5 w-3.5 text-[#0053DA]" />
                  Using your last settings:{" "}
                  <span className="font-semibold" data-testid="last-settings-summary">
                    {[lastApplied.applied.category, lastApplied.applied.condition].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <button type="button" onClick={undoLastSettings} className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-[#0053DA] hover:bg-slate-50" data-testid="last-settings-undo">
                  Undo
                </button>
              </div>
            ) : null}
            {/* Grid: category / condition / price / promo / sku */}
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldRow
                label="Category"
                hint="text path from AI"
                testid="field-category"
              >
                <div className="flex gap-2">
                  <Input value={draft.category || ""} onChange={(e) => patch({ category: e.target.value })} data-testid="input-category" />
                  <CopyButton text={draft.category} label="Copy" testid="copy-category-button" />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Input
                    value={draft.category_id || ""}
                    onChange={(e) => patch({ category_id: e.target.value.replace(/[^0-9]/g, "") })}
                    placeholder="eBay category ID (e.g. 9355)"
                    inputMode="numeric"
                    className="font-mono text-sm"
                    data-testid="input-category-id"
                  />
                  <a
                    href="https://www.ebay.com/sh/research"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap text-[11px] text-[#0053DA] hover:underline"
                    title="Find a category ID"
                  >
                    Find ID ↗
                  </a>
                </div>
                <CategorySuggestions
                  title={draft.title}
                  selectedId={(draft.category_id || "").trim()}
                  onPick={(id) => patch({ category_id: id })}
                />
                <p className="mt-1 text-[10px] text-slate-400">
                  Numeric eBay category ID. Auto-guessed on Publish — pick a suggestion above or paste your own.
                </p>
              </FieldRow>

              <FieldRow label="Condition" testid="field-condition">
                <Select value={draft.condition || ""} onValueChange={(v) => patch({ condition: v })}>
                  <SelectTrigger data-testid="select-condition"><SelectValue placeholder="Select condition" /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`condition-option-${c}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-3"><ConditionNotesField draft={draft} patch={patch} /></div>
                <div className="mt-3"><RecordModeCard draft={draft} patch={patch} /></div>
                {draft.card?.kind ? <div className="mt-3"><CardModeCard draft={draft} patch={patch} /></div> : null}
              </FieldRow>

              <FieldRow label="Price (USD)" testid="field-price">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
                  <Input
                    value={draft.price || ""}
                    onChange={(e) => patch({ price: e.target.value })}
                    data-testid="input-price"
                    inputMode="decimal"
                    placeholder="0.00"
                  />
                  <CopyButton text={draft.price} label="Copy" testid="copy-price-button" />
                </div>
                {draft.price_research && (draft.price_research.low || draft.price_research.avg || draft.price_research.high) ? (
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="field-research">
                    <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">Price research · tap to use</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {["low", "avg", "high"].map((k) => (
                        <button key={k} type="button" disabled={!draft.price_research[k]} data-testid={`research-${k}`}
                          onClick={() => patch({ price: Number(draft.price_research[k]).toFixed(2) })}
                          className={`rounded-md border px-1 py-1.5 text-center font-mono transition-colors disabled:opacity-40 ${Number(draft.price) === Number(draft.price_research[k]) ? "border-[#0053DA] bg-blue-50 text-[#0053DA]" : "border-slate-200 bg-white text-slate-800 hover:border-slate-300"}`}>
                          <span className="block text-[9px] uppercase tracking-widest text-slate-500">{k}</span>
                          <span className="text-sm font-semibold">{draft.price_research[k] ? `$${Number(draft.price_research[k]).toFixed(2)}` : "—"}</span>
                        </button>
                      ))}
                    </div>
                    {draft.price_research.note && <p className="mt-1.5 text-[11px] text-slate-500">{draft.price_research.note}</p>}
                  </div>
                ) : null}
                <div className="mt-2 flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Qty</span>
                  <div className="flex w-36 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => patch({ quantity: String(Math.max(1, (parseInt(draft.quantity, 10) || 1) - 1)) })}
                      className="flex h-9 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-40"
                      data-testid="input-quantity-dec"
                      aria-label="Decrease quantity"
                      disabled={(parseInt(draft.quantity, 10) || 1) <= 1}
                    >
                      −
                    </button>
                    <Input
                      value={draft.quantity ?? ""}
                      onChange={(e) => patch({ quantity: e.target.value.replace(/[^0-9]/g, "") })}
                      data-testid="input-quantity"
                      inputMode="numeric"
                      placeholder="Qty"
                      title="How many units are for sale under this listing"
                      className="flex-1 text-center"
                    />
                    <button
                      type="button"
                      onClick={() => patch({ quantity: String((parseInt(draft.quantity, 10) || 0) + 1) })}
                      className="flex h-9 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50"
                      data-testid="input-quantity-inc"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>
                <p className="mt-0.5 flex gap-3 text-[10px] text-slate-400">
                  <span>Qty defaults to 1 — multi-quantity creates a Fixed Price listing on eBay.</span>
                </p>
                {(() => {
                  const alert = computePriceAlert(draft);
                  if (!alert) return null;
                  return (
                    <div
                      className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                      data-testid="price-alert-banner"
                      role="status"
                    >
                      <span className="flex-1">
                        <strong>Comparable items averaged ${alert.median.toFixed(2)}</strong> on eBay
                        {alert.high ? <> (top sold ${alert.high.toFixed(2)})</> : null}
                        {" "}— that's <strong>{alert.upliftPct}% above</strong> your ${alert.price.toFixed(2)}.
                      </span>
                      <Button
                        size="sm"
                        onClick={() => patch({ price: alert.suggested.toFixed(2) })}
                        className="h-7 rounded-full bg-amber-600 text-white hover:bg-amber-700"
                        data-testid="price-alert-apply"
                        title={`Set price to the median comp ($${alert.suggested.toFixed(2)})`}
                      >
                        Use ${alert.suggested.toFixed(2)}
                      </Button>
                    </div>
                  );
                })()}
                <p className="mt-1 text-[10px] text-slate-400">
                  Nudges appear when a comparable sold for at least {getPriceAlertThresholdPct()}% more than your price.
                </p>
              </FieldRow>

              <VolumeDiscounts draft={draft} patch={patch} />

              <PackageFields draft={draft} patch={patch} />

              <WhatnotFormat draft={draft} patch={patch} />

              {draft.id && <VintedFields draft={draft} value={draft.vinted} onChange={(vinted) => patch({ vinted })} />}

              {draft.id && <MercariFields draft={draft} value={draft.mercari} onChange={(mercari) => patch({ mercari })} />}

              <FieldRow
                label="Minimum offer (USD)"
                testid="field-min-offer"
                hint={draft.min_offer || draft.auto_accept ? "Best Offer ON" : "Best Offer OFF"}
              >
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      value={draft.min_offer || ""}
                      onChange={(e) => patch({ min_offer: e.target.value.replace(/[^0-9.]/g, "") })}
                      placeholder="Auto-decline below"
                      data-testid="input-min-offer"
                      inputMode="decimal"
                    />
                    <p className="mt-0.5 text-[10px] text-slate-400">Auto-decline below</p>
                  </div>
                  <div>
                    <Input
                      value={draft.auto_accept || ""}
                      onChange={(e) => patch({ auto_accept: e.target.value.replace(/[^0-9.]/g, "") })}
                      placeholder="Auto-accept at or above"
                      data-testid="input-auto-accept"
                      inputMode="decimal"
                    />
                    <p className="mt-0.5 text-[10px] text-slate-400">Auto-accept at or above</p>
                  </div>
                </div>
                <p className="mt-1 text-[10px] text-slate-400">
                  Both are optional. If set, auto-accept must be ≥ minimum and ≤ price. Leave both blank to disable Best Offer.
                </p>
              </FieldRow>

              <FieldRow label="Promoted Ad Rate %" testid="field-promo">
                <div className="flex gap-2">
                  <Input value={draft.promotion_rate || ""} onChange={(e) => patch({ promotion_rate: e.target.value })} data-testid="input-promo" inputMode="decimal" />
                  <CopyButton text={draft.promotion_rate} label="Copy" testid="copy-promo-button" />
                </div>
              </FieldRow>

              <FieldRow label="SKU" testid="field-sku">
                <div className="flex gap-2">
                  <Input value={draft.sku || ""} onChange={(e) => patch({ sku: e.target.value })} data-testid="input-sku" />
                  <CopyButton text={draft.sku} label="Copy" testid="copy-sku-button" />
                </div>
              </FieldRow>

              <FieldRow label="UPC / ISBN" testid="field-barcode">
                <div className="flex gap-2">
                  <Input value={draft.barcode || ""} inputMode="numeric" maxLength={17} placeholder="Blank = Does not apply"
                    onChange={(e) => patch({ barcode: e.target.value.replace(/[^0-9Xx-]/g, "") })} data-testid="input-barcode" />
                  <CopyButton text={draft.barcode} label="Copy" testid="copy-barcode-button" />
                </div>
              </FieldRow>

              {!isAssistant && <FieldRow label="Item cost" testid="field-item-cost">
                <Input value={draft.item_cost || ""} inputMode="decimal" placeholder="What you paid (optional)"
                  onChange={(e) => patch({ item_cost: e.target.value.replace(/[^0-9.]/g, "") })} data-testid="input-item-cost" />
              </FieldRow>}

            </div>

            {/* Item specifics */}
            <FieldRow
              label="Item specifics"
              testid="field-specifics"
              action={
                <div className="flex gap-1">
                  <CopyButton
                    text={(draft.item_specifics || []).map((s) => `${s.key}: ${s.value}`).join("\n")}
                    label="Copy all"
                    testid="copy-specifics-button"
                  />
                  <Button size="sm" variant="outline" onClick={addSpecific} className="h-7 rounded-full" data-testid="add-specific-button">
                    <Plus className="mr-1 h-3 w-3" /> Add
                  </Button>
                </div>
              }
            >
              <AspectsAssist
                aspects={aspects}
                loading={loadingAspects}
                categoryId={(draft.category_id || "").trim()}
                specifics={draft.item_specifics || []}
                onFill={(newSpecifics) => patch({ item_specifics: newSpecifics })}
              />
              <div className="space-y-2">
                {(draft.item_specifics || []).length === 0 && (
                  <p className="text-xs text-slate-500">No specifics yet. Add key-value pairs like Brand, Model, Color.</p>
                )}
                {(draft.item_specifics || []).map((s, i) => {
                  const aspect = (aspects?.aspects || []).find((a) => a.name.toLowerCase() === (s.key || "").toLowerCase());
                  const hasSuggestions = aspect && aspect.values && aspect.values.length > 0;
                  return (
                    <div key={`${s.key}-${i}`} className="grid grid-cols-[1fr,1.5fr,auto] gap-2">
                      <Input placeholder="Key" value={s.key} onChange={(e) => patchSpecific(i, "key", e.target.value)} data-testid={`specific-key-${i}`} />
                      {hasSuggestions ? (
                        <div className="relative">
                          <Input
                            list={`aspect-values-${i}`}
                            placeholder="Value"
                            value={s.value}
                            onChange={(e) => patchSpecific(i, "value", e.target.value)}
                            data-testid={`specific-value-${i}`}
                          />
                          <datalist id={`aspect-values-${i}`}>
                            {aspect.values.slice(0, 40).map((v) => <option key={v} value={v} />)}
                          </datalist>
                        </div>
                      ) : (
                        <Input placeholder="Value" value={s.value} onChange={(e) => patchSpecific(i, "value", e.target.value)} data-testid={`specific-value-${i}`} />
                      )}
                      <button
                        type="button"
                        onClick={() => removeSpecific(i)}
                        className="rounded-md border border-slate-200 px-2 text-slate-400 hover:text-red-600"
                        data-testid={`remove-specific-${i}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </FieldRow>

            {/* Description */}
            <FieldRow
              label="Description"
              testid="field-description"
              action={<CopyButton text={draft.description} label="Copy" testid="copy-description-button" />}
            >
              <Textarea
                value={draft.description || ""}
                onChange={(e) => patch({ description: e.target.value })}
                className="min-h-[160px]"
                data-testid="input-description"
              />
            </FieldRow>
          </div>
        )}
      </section>
    </div>
  );
}

function FieldRow({ label, hint, action, children, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <Label className="truncate text-xs font-mono uppercase tracking-wider text-slate-500">{label}</Label>
        <div className="flex items-center gap-2">
          {hint && <span className="text-[11px] font-mono text-slate-400">{hint}</span>}
          {action}
        </div>
      </div>
      {children}
    </div>
  );
}

